import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, Check, X } from 'lucide-react';

/**
 * SEO landing page — "Sony FX3 ou FX6 : quelle caméra choisir ?".
 * Comparative article targeting comparison-shopping intent for two of our
 * most rented cameras. Ends with a CTA linking to the catalogue.
 */
const SonyFx3VsFx6 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = 'https://www.puzzlevideo.fr/guides/sony-fx3-vs-fx6';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Sony FX3 ou FX6 : quelle caméra choisir pour votre tournage ?',
    description:
      'Comparatif détaillé Sony FX3 vs FX6 : capteur, autofocus, ergonomie, autonomie, prix de location à Paris. Puzzle Video vous aide à choisir.',
    author: { '@type': 'Organization', name: 'Puzzle Video' },
    publisher: {
      '@type': 'Organization',
      name: 'Puzzle Video',
      logo: {
        '@type': 'ImageObject',
        url: 'https://for-rental-staging.s3.eu-west-3.amazonaws.com/eqgootqxdu25amnn1whuqc1u53tk',
      },
    },
    mainEntityOfPage: canonical,
    datePublished: '2026-02-11',
    dateModified: '2026-02-11',
    inLanguage: 'fr-FR',
  };

  const rows = [
    ['Capteur',              'Full Frame Exmor R',   'Full Frame Exmor R'],
    ['Résolution max',       '4K 120p',              '4K 120p'],
    ['Enregistrement interne','16-bit RAW via HDMI', '16-bit RAW via HDMI + XAVC S-I 4K 240p'],
    ['ISO natif dual',       '800 / 12800',          '800 / 12800'],
    ['Autofocus',            'AI dédié',             'AI dédié + phase detection'],
    ['Poignée / top handle', 'Poignée XLR (2 canaux)','Top handle intégrée (2 canaux XLR)'],
    ['Filtre ND intégré',    'Non',                  'Oui, ND variable (2-7 stops)'],
    ['Autonomie',            'Batterie NP-FZ100',    'Batterie BP-U60 (~1,5×)'],
    ['Écran',                'LCD tactile 3"',       'LCD orientable 3,5"'],
    ['Poids nu',             '640 g',                '890 g'],
    ['Timecode',             'BNC en accessoire',    'BNC natif'],
    ['Idéal pour',           'Solo op, gimbal, doc léger', 'Fiction, doc long, plateau plus formalisé'],
    ['Tarif location HT/j',  'à partir de 120 €',    'à partir de 170 €'],
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Sony FX3 ou FX6 : quelle caméra choisir ? — Guide comparatif"
        description="Comparatif Sony FX3 vs FX6 : capteur, autofocus, filtre ND, ergonomie, prix location à Paris. Le guide Puzzle Video pour bien choisir."
        url={canonical}
        jsonLd={jsonLd}
      />
      <Helmet>
        <link rel="canonical" href={canonical} />
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground mb-6" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link>
          <span className="mx-2">/</span>
          <Link to="/guides" className="hover:text-primary">Guides</Link>
          <span className="mx-2">/</span>
          <span>Sony FX3 ou FX6</span>
        </nav>

        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Comparatif caméra</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">
            Sony FX3 ou FX6&nbsp;: quelle caméra choisir&nbsp;?
          </h1>
          <p className="text-lg text-muted-foreground">
            Les FX3 et FX6 partagent le même capteur Full Frame et une grande partie de leur ADN
            Cine, mais s&apos;adressent à des usages différents. Voici comment décider laquelle
            louer pour votre projet.
          </p>
        </header>

        <section className="mb-10 space-y-4">
          <h2 className="text-2xl font-bold mt-8 mb-3">La FX3 en une phrase</h2>
          <p>
            Une <strong>caméra cinéma dans un boîtier hybride</strong>&nbsp;: parfaite pour le
            documentaire léger, les tournages solo op, la vidéo sur gimbal ou drone où le poids
            compte plus que la fioriture broadcast. Autonomie corrects, workflow proche de l&apos;A7S III.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-3">La FX6 en une phrase</h2>
          <p>
            La <strong>vraie caméra de plateau compacte</strong>&nbsp;: filtre ND variable natif,
            batteries BP-U grande capacité, top handle avec XLR intégrés, timecode BNC. Elle
            remplace avantageusement une FS7 pour la plupart des tournages fiction et
            documentaire moyen/long format.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-3">Comparatif technique</h2>
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-3 font-semibold">Critère</th>
                  <th className="text-left p-3 font-semibold">Sony FX3</th>
                  <th className="text-left p-3 font-semibold">Sony FX6</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([k, a, b]) => (
                  <tr key={k} className="border-t border-border">
                    <td className="p-3 font-medium">{k}</td>
                    <td className="p-3 text-muted-foreground">{a}</td>
                    <td className="p-3 text-muted-foreground">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-3">Choisir la FX3 quand…</h2>
          <ul className="space-y-2">
            {[
              'Vous tournez seul, avec un rig minimaliste ou un gimbal.',
              'Le poids et la discrétion priment (interview improvisée, tournage guerilla).',
              'Vous avez déjà un workflow S-Log 3 / S-Cinetone et vous voulez rester dans l’écosystème hybrid.',
              'Le budget est serré (~50 € HT/j de moins que la FX6).',
            ].map((s) => (
              <li key={s} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-1 shrink-0" /> {s}</li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-3">Choisir la FX6 quand…</h2>
          <ul className="space-y-2">
            {[
              'Vous tournez avec un plateau formel (chef op + assistant).',
              'Le tournage nécessite du son intégré en 2 canaux XLR sans adaptateur.',
              'Les ND variables sont indispensables (extérieur avec exposition changeante).',
              'L’autonomie longue durée compte (batteries BP-U).',
              'Le workflow fiction/pub impose du timecode BNC pour la synchro son / caméras multiples.',
            ].map((s) => (
              <li key={s} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-1 shrink-0" /> {s}</li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-3">Ce qui n&apos;a PAS d&apos;impact décisif</h2>
          <ul className="space-y-2">
            {[
              'La qualité d’image brute — les deux capteurs sont identiques.',
              'La haute sensibilité — ISO natif dual identique.',
              'Le codec — le S-Cinetone/S-Log 3 sont livrés sur les deux.',
            ].map((s) => (
              <li key={s} className="flex items-start gap-2"><X className="w-4 h-4 text-muted-foreground mt-1 shrink-0" /> {s}</li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-3">Louer une FX3 ou FX6 à Paris</h2>
          <p>
            Nous stockons plusieurs exemplaires des deux modèles. Retrait à Paris 12e (65 rue
            Baron Leroy) ou livraison sur toute l&apos;Île-de-France. Toutes les caméras sont
            livrées prêtes à tourner (poignée, cage, batteries de base, chargeur, carte média
            selon disponibilité).
          </p>
        </section>

        {/* CTA */}
        <div className="mt-12 p-6 bg-gray-50 border-l-4 border-primary space-y-3">
          <h3 className="text-xl font-bold">Prêt à réserver&nbsp;?</h3>
          <p className="text-sm text-muted-foreground">
            Consultez les fiches détaillées et le prix à jour de chaque modèle&nbsp;:
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/catalogue?search=FX3">
              <Button className="rounded-none">Voir la Sony FX3 <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <Link to="/catalogue?search=FX6">
              <Button className="rounded-none" variant="outline">Voir la Sony FX6 <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default SonyFx3VsFx6;
